import { Flex, Text } from '@components';
import LocalLoader from '@components/AppLoader/LocalLoader';
import {
  useGetEmbeddedContractQuery,
  useGetInvestmentIntentQuery,
} from '@features/api/investmentSlice';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { Steps } from '../Progress/Progress.types';
import { COMPLETED_EVENT, ENVELOPE_EXPIRED } from './ContractSigning.const';
import { Container, MessageContainer } from './ContractSigning.styles';

export const RETRY_INTERVAL_MS = 1000 * 60;
export const MAX_RETRIES = 5;
export const RETRIABLE_STATUS_CODES = [503, 433, 436, 400];

const ContractSigning: React.FC = () => {
  const navigate = useNavigate();

  const { productId } = useParams();

  const [isRetrying, setIsRetrying] = useState(false);
  const [timesRefetched, setTimesRefetched] = useState(0);
  const [showComeBackLater, setShowComeBackLater] = useState(false);
  const [iFrameLoaded, setIFrameLoaded] = useState(false);

  const { data: investmentIntent } = useGetInvestmentIntentQuery({
    productId: productId ?? '',
  });
  const { data, error, isFetching, isLoading, refetch } =
    useGetEmbeddedContractQuery(
      { intentId: investmentIntent?.id ?? '' },
      {
        refetchOnMountOrArgChange: true,
        skip: !investmentIntent,
      }
    );

  const { url: docusignUrl } = data ?? {};

  useEffect(() => {
    setIFrameLoaded(false);
  }, [docusignUrl]);

  useEffect(() => {
    const typedError = error as any;
    const shouldRefetch =
      RETRIABLE_STATUS_CODES.includes(typedError?.status) &&
      timesRefetched < MAX_RETRIES;
    if (shouldRefetch) {
      setIsRetrying(true);
      const id = setTimeout(() => {
        if (shouldRefetch) {
          refetch();
          setTimesRefetched((timesRefetched) => timesRefetched + 1);
        }
      }, RETRY_INTERVAL_MS);

      return () => clearTimeout(id);
    } else if (
      RETRIABLE_STATUS_CODES.includes(typedError?.status) &&
      timesRefetched >= MAX_RETRIES
    ) {
      setShowComeBackLater(true);
    } else if (!error) {
      setIsRetrying(false);
      setShowComeBackLater(false);
      setTimesRefetched(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    const handler = (ev: MessageEvent<{ type: string; message: string }>) => {
      if (ev.data.message === COMPLETED_EVENT) {
        onNext();
      } else if (ev.data.message === ENVELOPE_EXPIRED) {
        refetch();
      }
    };

    window.addEventListener('message', handler);

    return () => window.removeEventListener('message', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onNext = () =>
    navigate(`/investment-subscription/${productId}/progress`, {
      state: {
        completedSteps: [
          Steps.Details,
          Steps.IdentityVerification,
          Steps.ContractSignature,
        ],
        progress: 10,
      },
      replace: true,
    });

  const showLoader = isFetching || isLoading || !iFrameLoaded;
  if (showComeBackLater) {
    return (
      <MessageContainer padding='20%'>
        <Text variant='h5' fontWeight='fontWeightMedium'>
          <FormattedMessage id='investment.contractSigning.pleaseComeBackLater' />
        </Text>
      </MessageContainer>
    );
  }

  return (
    <Container>
      {isRetrying && (
        <MessageContainer gap={14}>
          <Flex flex={0} position='relative'>
            <LocalLoader />
          </Flex>
          <Text variant='h5' fontWeight='fontWeightMedium'>
            <FormattedMessage id='investment.contractSigning.buildingAgreement' />
          </Text>
        </MessageContainer>
      )}
      {showLoader && !isRetrying && <LocalLoader />}

      <>
        <iframe
          src={docusignUrl}
          title='DocuSign'
          style={{ height: '100%', width: '100%', border: 'none' }}
          onLoad={() => setIFrameLoaded(true)}
        />
      </>
    </Container>
  );
};

export default ContractSigning;
